/// \file navbar.tsx
///
/// \par Copyright Notice:
/// Copyright (c) 2023 by Oshkosh Corporation.
/// All Rights Reserved.
///
/// \par
/// The information contained herein is confidential and proprietary property
/// of Oshkosh Corporation, and may be privileged.  The dissemination,
/// distribution, use, copying, transfer, or disclosure of such information is
/// strictly prohibited except by express written agreement with Oshkosh
/// Corporation, or with one of its subsidiaries.
import { ADVANCEDUSER, SYSTEMUSER, USERWRITENUMBERS } from '../types/global';
import { Fragment } from 'react';
import { SetFilter } from './filter/setFilter';
import { useNavigate, useSubmit } from 'react-router-dom';
import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
  Menu,
  MenuItem,
  MenuSelectEvent,
} from '@progress/kendo-react-layout';
import React, {useState, useEffect} from 'react';
import { PopupFormatter } from '../views/anywhere-popups/popups/popupFormatter';
import { Popup } from './popup';
import profileUseStates from '../views/anywhere-popups/useStates/profileUseStates';
import passwordUseStates from '../views/anywhere-popups/useStates/passwordUseStates';
import preferencesUseStates from '../views/anywhere-popups/useStates/preferencesUseStates';
import localDBCalls from "../services/localDBCalls";
import { SearchDropdownBar } from './filter/searchDropdownBar';
import CheckingPasswords from '../services/checkingPassword';
import { CheckDataExists } from './checkDataExists';
import updateCookie from '../services/updateCookie';
import getUserInfo from '../services/getUserInfo';
const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const submit = useSubmit();
  const [user, setUser] = useState<any>(localStorage.getItem('user'));
  const [userLevel, setUserLevel] = useState<string>("");
  const [userAccessType, setUserAccessType] = useState<string>("");
  const [userCustomer, setUserCustomer] = useState<string>("");
  const [currentUser, setCurrentUser] = useState({profile:{}});
  const [currentUserProfile,setCurrentUserProfile] = useState<{firstName:string,lastName:string,email:string,office:string,mobile:string}>({
    firstName:"",
    lastName:"",
    email:"",
    office:"",
    mobile:""
  });

  const updateUserLevel = async () => {
    if(user){
      const userLevelReturn = await getUserInfo.getLevel(navigate);
      if(userLevel!=userLevelReturn){
        setUserLevel(userLevelReturn)
      }
      const userAccessTypeReturn = await getUserInfo.getAccessType(navigate);
      if(userAccessType!=userAccessTypeReturn){
        setUserAccessType(userAccessTypeReturn)
      }
      const userCustomerReturn = await getUserInfo.getCustomer(navigate);
      if(userCustomer!=userCustomerReturn){
        setUserCustomer(userCustomerReturn)
      }
    }
  }

  //runs on every page change...for within page tab changes, that needs to happen in mini nav bar
  updateCookie.updateTokenTime();

  const pathname = window.location.pathname
  if(pathname=="/"){
    const currentStoredUser = localStorage.getItem('user')
    if(currentStoredUser!=user){
      setUser(currentStoredUser)
      updateUserLevel()
    }
  }
  else if(pathname=="/auth/signin"){
    const currentStoredUser = ""
    if(currentStoredUser!=user){
      setUser(currentStoredUser)
      updateUserLevel()
    }
  }

  const onSelect = async(event: MenuSelectEvent) => {
    if(event.item?.data?.route) {
      // do check
      const validToken = await localDBCalls.checkUserIsValid();
      const validPass = localDBCalls.checkPassIsValid(document.cookie); //0 == good password, 1 == update password, 2 == signup
      if (event.item.data.route != "/auth/signin" && validToken!=1){
        console.log("LOGGED OUT");
        navigate("/auth/signin");
      }
      else if ((event.item.data.route != "/auth/signup" && event.item.data.route != "/auth/createpassword") && validPass!=0){
        console.log("LOGGED OUT");
        if(validPass==1){
          navigate("/auth/createpassword");
        }
        else{
          navigate("/auth/signup");
        }
      } else {
      navigate(event.item.data.route);
      }
    } else if (event.item?.data?.action) {
      submit(null, { method: 'post', action: event.item.data.action });
    }
    else if (event.item?.data?.popup) {
      if(user){
        const popupType = event.item.data.popup;
        if(popupType=="manage-profile"){
          setVisibleProfile(true);
        }
        else if(popupType=="change-preferences"){
          setVisiblePreferences(true);
        }
        else if(popupType=="change-password"){
          setVisiblePassword(true);
        }
      }
      else{
        console.log("no user for user changes")
      }
    }
    const checkUser = localStorage.getItem('user')
    if(checkUser!=user){
      setUser(checkUser)
    }
  };

//popup
  //profile
  const updateUserData = async () => {
    if(user){
      const userData = await localDBCalls.toUser("user/all","get", document.cookie);
      if(userData.message=="valid"){
        setCurrentUser(userData.data[0]);
        setCurrentUserProfile(userData.data[0].profile);
        return userData;
      }
    }
  }

  const updateUserProfile = async(profileValues:any) => {
    const updateUser = currentUser
    updateUser.profile = profileValues;
    let queryString = JSON.stringify(updateUser);
    queryString=queryString.slice(1,-1);
    queryString=queryString.replace(/:/g, "=")
    queryString=queryString.replace(/,/g, "&");
    queryString=queryString.replace("_id", "id");
    const querySplit = queryString.split("{");
    querySplit[0]=querySplit[0].replace(/"/g, "");
    const querySplit2 = querySplit[1].split("}");
    querySplit2[0] = querySplit2[0].replaceAll("=",":");
    querySplit2[0] = querySplit2[0].replaceAll("&",",");
    querySplit2[0] = querySplit2[0].replace(/"/g, '"');
    querySplit2[1] = querySplit2[1].replace(/"/g, "");
    queryString = querySplit[0]+"{"+querySplit2[0]+"}"+querySplit2[1]
    const userObject = await localDBCalls.getId("",document.cookie)
    let userId = "";
    if(userObject.data){
      userId = userObject.data
    }
    const updateObject = await localDBCalls.updateProfile(document.cookie,userId,queryString);
  }
  
  useEffect(() => {
    updateUserData();
    updateUserLevel();
    checkValidUser();
  }, [user]);

  const checkValidUser = () => {
    setTimeout(
      ()=>{
        const pathname = window.location.pathname.split("/");
        if(pathname.length<=1){
          updateUserData();
          updateUserLevel();
        }
        else if(pathname[1]!="auth" || (pathname[2]!="signin" && pathname[2]!="signup" && pathname[2]!="createpassword")){
          updateUserData();
          updateUserLevel();
        }
        checkValidUser();
      },1000*60*1
    )
  }
  
  const [visibleProfile, setVisibleProfile] = useState(false);
  const profileId = "popup-profile";
  
  const onInputChangeProfile = () => {
    const firstNameElement = document.getElementById(profileId+"-firstName-input") as HTMLInputElement;
    const lastNameElement = document.getElementById(profileId+"-lastName-input") as HTMLInputElement;
    const emailElement = document.getElementById(profileId+"-email-input") as HTMLInputElement;
    const officeElement = document.getElementById(profileId+"-office-input") as HTMLInputElement;
    const mobileElement = document.getElementById(profileId+"-mobile-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-profile-submission-button");
    if(firstNameElement && lastNameElement && emailElement && officeElement && mobileElement && buttonElement){
      const firstNameValue = firstNameElement.value;
      const lastNameValue = lastNameElement.value;
      const emailValue = emailElement.value;
      const officeValue = officeElement.value;
      const mobileValue = mobileElement.value;
      if(firstNameValue && lastNameValue && emailValue && officeValue && mobileValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
    else{
    }
  };

  const setVisibleProfileFalse = () => {
    setVisibleProfile(false);
  };

  const {
    setCurrentProfile,
    pairsProfile,
  } = profileUseStates(onInputChangeProfile, 
    currentUserProfile
);
  const updateProfile = () => {
    const firstNameElement = document.getElementById(profileId+"-firstName-input") as HTMLInputElement;
    const lastNameElement = document.getElementById(profileId+"-lastName-input") as HTMLInputElement;
    const emailElement = document.getElementById(profileId+"-email-input") as HTMLInputElement;
    const officeElement = document.getElementById(profileId+"-office-input") as HTMLInputElement;
    const mobileElement = document.getElementById(profileId+"-mobile-input") as HTMLInputElement;
    if(firstNameElement && lastNameElement && emailElement && officeElement && mobileElement){
      const firstNameValue = firstNameElement.value;
      const lastNameValue = lastNameElement.value;
      const emailValue = emailElement.value;
      const officeValue = officeElement.value;
      const mobileValue = mobileElement.value;
      if(firstNameValue && lastNameValue && emailValue && officeValue && mobileValue){
        const currentValues = {
          firstName:firstNameValue,
          lastName:lastNameValue,
          email:emailValue,
          office:officeValue,
          mobile:mobileValue,
        };
        setCurrentProfile(currentValues);
        setCurrentUserProfile(currentValues);
        updateUserProfile(currentValues);
        setVisibleProfileFalse();
      }
    }
  };

  const profileDialog = <PopupFormatter
                      id = {profileId}
                      pairs={pairsProfile}
                      onInputChange={onInputChangeProfile}
                      />;

  const extraButtonProfile = [
    {id:"new-profile-submission-button", className:"defaultbutton unclickable", onClick:updateProfile, name:"OK"}
  ];

  const profileTitle = "Manage Profile";

  //preferences
  const [visiblePreferences, setVisiblePreferences] = useState(false);
  const prefId = "popup-preferences";  
  const {
    timeZoneValue,         
    deliveryTimeValue, 
    csvValue,
    deliveryDayValue,
    mapAppValue,
    mapTypeValue,
    recordsValue,
    decimalsValue,
    unitsValue,
    firstDayValue,
    dateNumberFormatValue,
    setCurrentPreferences,
    pairsPreferences,
  } = preferencesUseStates();

  const onInputChangePreferences = () => {
    const faultElement = document.getElementById(prefId+"-faults-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-preferences-submission-button");
    if(faultElement && buttonElement){
      const faultsValue = faultElement.value;
      if(faultsValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
  };

  const setVisiblePreferencesFalse = () => {
    setVisiblePreferences(false);
  };

  const updatePreferences = () => {
    const faultElement = document.getElementById(prefId+"-faults-input") as HTMLInputElement;
    if(faultElement){
      const faultsValue = faultElement.value;
      if(faultsValue){
        const currentValues = {
          faults:faultsValue,
          timeZone:timeZoneValue,
          deliveryTime:deliveryTimeValue,
          csv:csvValue,
          deliveryDay:deliveryDayValue,
          mapApp:mapAppValue,
          mapType:mapTypeValue,
          records:recordsValue,
          decimals:decimalsValue,
          units:unitsValue,
          firstDay:firstDayValue,
          dateNumberFormat:dateNumberFormatValue,
        };
        setCurrentPreferences(currentValues);
        setVisiblePreferencesFalse();
      }
    }
  };

  const prefDialog = <PopupFormatter
                      id = {prefId}
                      pairs={pairsPreferences}
                      onInputChange={onInputChangePreferences}
                      />;

  const extraButtonPreference = [
    {id:"new-preferences-submission-button", className:"defaultbutton unclickable", onClick:updatePreferences, name:"OK"}
  ];

  const prefTitle = "Update Preferences";
  
  //password
  const [visiblePassword, setVisiblePassword] = useState(false);
  const passwordId = "popup-password";
  const {
    questionValue,
    setCurrentPassword,
    pairsPassword,
    seterrorPopup,
  } = passwordUseStates();
  
  const onInputChangePassword = () => {
    const currentPasswordElement = document.getElementById(passwordId+"-currentPassword-input") as HTMLInputElement;
    const newPasswordElement = document.getElementById(passwordId+"-newPassword-input") as HTMLInputElement;
    const confirmNewPasswordElement = document.getElementById(passwordId+"-confirmNewPassword-input") as HTMLInputElement;
    const resetAnswerElement = document.getElementById(passwordId+"-resetAnswer-input") as HTMLInputElement;
    const buttonElement = document.getElementById("new-password-submission-button");
    if(currentPasswordElement && newPasswordElement && confirmNewPasswordElement && resetAnswerElement && buttonElement){
      const currentPasswordValue = currentPasswordElement.value;
      const newPasswordValue = newPasswordElement.value;
      const confirmNewPasswordValue = confirmNewPasswordElement.value;
      const resetAnswerValue = resetAnswerElement.value;
      if(currentPasswordValue && newPasswordValue && confirmNewPasswordValue && resetAnswerValue){
        buttonElement.classList.add("clickable");
        buttonElement.classList.remove("unclickable");
      }
      else{
        buttonElement.classList.add("unclickable");
        buttonElement.classList.remove("clickable");
      }
    }
  };

  const setVisiblePasswordFalse = () => {
    setVisiblePassword(false);
  };

  const updatePassword = async() => {
    const currentPasswordElement = document.getElementById(passwordId+"-currentPassword-input") as HTMLInputElement;
    const newPasswordElement = document.getElementById(passwordId+"-newPassword-input") as HTMLInputElement;
    const confirmNewPasswordElement = document.getElementById(passwordId+"-confirmNewPassword-input") as HTMLInputElement;
    const resetAnswerElement = document.getElementById(passwordId+"-resetAnswer-input") as HTMLInputElement;
    const errorElement = document.getElementById(passwordId+"-errorCode");
    if(currentPasswordElement && newPasswordElement && confirmNewPasswordElement && resetAnswerElement && errorElement){
      const currentPasswordValue = currentPasswordElement.value;
      const newPasswordValue = newPasswordElement.value;
      const confirmNewPasswordValue = confirmNewPasswordElement.value;
      const resetAnswerValue = resetAnswerElement.value;
      let errorMessage = "";
      if(currentPasswordValue && newPasswordValue && confirmNewPasswordValue && resetAnswerValue){
        if(newPasswordValue==confirmNewPasswordValue && CheckingPasswords.checkValidPassword(newPasswordValue)){
          const currentValues = {
            currentPassword:currentPasswordValue,
            newPassword:newPasswordValue,
            confirmNewPassword:confirmNewPasswordValue,
            resetAnswer:resetAnswerValue,
            question:questionValue,
          };
          //send new password to backend
          const userGet = await localDBCalls.getId("from create password", document.cookie);
          let userId = "";
          if(userGet.data){
            userId = userGet.data;
          }
          const getTime = new Date();
          const stringTime = getTime.getTime()
          const newProfile = 'profile={"agreeToTerms":'+stringTime+'}'
          //change to update 2 things: password and profile
          const resetPasswordReturn = await localDBCalls.updatePassword("",document.cookie,userId,currentPasswordValue,newPasswordValue)
          if(resetPasswordReturn.data!=undefined && resetPasswordReturn.content!=undefined){
            try{
              if(
                resetPasswordReturn.content.data.reason != "Invalid password" && 
                resetPasswordReturn.content.data.reason != "Incorrect password" && 
                resetPasswordReturn.content.data.reason != "password used recently"){
                setCurrentPassword(currentValues);
                setVisiblePasswordFalse();
              }
              else{
                errorMessage = resetPasswordReturn.content.data.reason;
              }
            }
            catch{
              errorMessage = resetPasswordReturn.content.message;
            }
          }
          else{
            errorMessage = "Not Currently Handled";
          }
          errorElement.innerText = errorMessage;
          seterrorPopup(true)
        }
        else{
          newPasswordElement.value="";
          confirmNewPasswordElement.value="";
          const buttonElement = document.getElementById("new-password-submission-button");
          if(buttonElement){
            buttonElement.classList.add("unclickable");
            buttonElement.classList.remove("clickable");
            errorElement.innerText = "new passwords did not match: ";
            seterrorPopup(true)
          }
        }
      }
    }
  };

  const passwordDialog = <PopupFormatter
                      id = {passwordId}
                      pairs={pairsPassword}
                      onInputChange={onInputChangePassword}
                      />;

  const extraButtonPassword = [
    {id:"new-password-submission-button", className:"defaultbutton unclickable", onClick:updatePassword, name:"OK"}
  ];

  const passwordTitle = "Change Password";


  
  const visible = visibleProfile || visiblePreferences || visiblePassword;
  const title = visibleProfile ? profileTitle : visiblePreferences ? prefTitle : passwordTitle;
  const dialog = visibleProfile ? profileDialog : visiblePreferences ? prefDialog : passwordDialog;
  const toggleDialogClose = visibleProfile ? setVisibleProfileFalse : visiblePreferences ? setVisiblePreferencesFalse : setVisiblePasswordFalse;
  const closeButtonName = "CANCEL";
  const extraButtons = visibleProfile ? extraButtonProfile : visiblePreferences ? extraButtonPreference : extraButtonPassword;


  const [searchableAssets, setsearchableAssets] = useState<{type:string,name:string,redirect:string}[]>([]);
  const updateNavbar = async () => {
    let customer = "";
    if(localStorage.getItem("customer_id")){
      const customerAttempt = localStorage.getItem("customer_id");
      if(customerAttempt){
        customer = customerAttempt;
      }
    }
    const customers = await localDBCalls.toCustomer("/all","get/all",document.cookie,"","");
    const assets = await localDBCalls.toAsset("asset/all", "get", document.cookie, customer,"");
    let assetsName = [];
    if(assets && assets.data){
      for (let i=0; i<assets.data.length; i++) {
        //customer try to use customer_name
        if(CheckDataExists({data:assets,checkString:"assets.data["+i+"].info.vin"})){
          assetsName.push({type:"asset",name:assets.data[i].info.vin,redirect:assets.data[i].info.vin});
          if(CheckDataExists({data:assets,checkString:"assets.data["+i+"].info.license"})){
            assetsName.push({type:"asset",name:assets.data[i].info.license,redirect:assets.data[i].info.vin});
          }
          if(ADVANCEDUSER.includes(userLevel.toLowerCase())){
            if(CheckDataExists({data:assets,checkString:"assets.data["+i+"].info.name"})){
              assetsName.push({type:"asset",name:assets.data[i].info.name,redirect:assets.data[i].info.vin});
            }
          }
          else{
            if(CheckDataExists({data:assets,checkString:"assets.data["+i+"].info.customer_name"})){
              assetsName.push({type:"asset",name:assets.data[i].info.customer_name,redirect:assets.data[i].info.vin});
            }
            else if(CheckDataExists({data:assets,checkString:"assets.data["+i+"].info.name"})){
              assetsName.push({type:"asset",name:assets.data[i].info.name,redirect:assets.data[i].info.vin});
            }
          }
        }
      }
    }
    if(customers && customers.data){
      for (let j=0; j<customers.data.length; j++) {
        assetsName.push({type:"customer",name:customers.data[j].details.name,redirect:customers.data[j].id});
      }
    }
    if (JSON.stringify(allAssets) != JSON.stringify(assetsName)) {
      setsearchableAssets(assetsName);
    }
  }

  const [allAssets, setallAssets] = useState(searchableAssets);

  useEffect(() => {
    updateNavbar();
  }, [allAssets]);

  const targetFilterChange = (event:{filter:{field:string,operator:string,value:string}}) => {
    const filter=event.filter;
    filter.field="name";
    const currentArray=allAssets;
    const totalArray=searchableAssets;
    const setFilterReturn = SetFilter({filter,currentArray,totalArray});
    setallAssets(setFilterReturn.filteredArray);
  };

  const searchSelect = async (e:any) => {
    const valueUsing = e.value;
    if(valueUsing && document.cookie.split(";")[0].split("=")[1]){
      if(valueUsing.type=="asset"){
        navigate("/fleet/asset/"+valueUsing.redirect)
      }
      else if(valueUsing.type=="customer"){
        const customerCrumbs = localStorage.getItem('customer-crumbs');
        let crumbJSON = [{id:valueUsing.redirect,text:valueUsing.name,action:"customer"}]
        //change customer on backend
        const returnCustomer = await localDBCalls.changeCustomer("",document.cookie,valueUsing.redirect);
        if(returnCustomer && returnCustomer.valid && returnCustomer.valid==true){
          if(customerCrumbs){
            crumbJSON = JSON.parse(customerCrumbs)
            crumbJSON.push({id:valueUsing.redirect,text:valueUsing.name,action:"customer"});
          }
          //{id:customer_id,text:name,action:customer}
          localStorage.setItem('customer-crumbs',JSON.stringify(crumbJSON));
          localStorage.setItem("customer_id",valueUsing.redirect);
          navigate("/");
        }
        else{
          console.log("something went wrong: ")
        }
      }
    }
    else{
      console.log("search without token")
    }
  };
  const id = "navbar";

  const devEnv = process.env.REACT_APP_DEV_ENV;

  let titlename = "CMS Portal - LOCAL";
  if(devEnv=="dev"){
    titlename = "CMS Portal - DEV";
  }
  if(devEnv=="prod"){
    titlename = "CMS Portal";
  }

  return (
    <Fragment>
      <AppBar style={{ background: 'white' }} >
        <AppBarSpacer style={{ width: 4 }} />
        <AppBarSection>
          <h1 className="title">{titlename}</h1>
        </AppBarSection>
        <AppBarSpacer />
        {user
              ?
        <AppBarSection>
        <div className={id+"-search-dropdown"}>
          <SearchDropdownBar
          id = {id+"-search-dropdown-combobox"}
          onFilterChange={targetFilterChange}
          onChange={searchSelect}
          data={allAssets}
          value={""}
          placeholder={"Search"}
          showcase={"name"}
          fullsize={true}
          />
        </div>
        </AppBarSection>
        :
        <AppBarSection></AppBarSection>
        }
        <AppBarSection>
          <Menu onSelect={onSelect}>
            <MenuItem text='Home' data={{ route: '/' }} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
            {(USERWRITENUMBERS.includes(userAccessType.toString().toLowerCase())) &&
              <MenuItem text='Admin' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
                <MenuItem text='Security' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
                  <MenuItem text='Security Users' data={{ route: '/security/securityUsers' }} cssClass = "MenuItem" />
                  {(SYSTEMUSER.includes(userLevel.toLowerCase())) && 
                    <MenuItem text='Security Logs' data={{ route: '/security/securityLogs' }} cssClass = "MenuItem" />
                  }
                  {(SYSTEMUSER.includes(userLevel.toLowerCase())) && 
                    <MenuItem text='Security Lockouts' data={{ route: '/security/securityLockouts' }} cssClass = "MenuItem" />
                  }
                  {(SYSTEMUSER.includes(userLevel.toLowerCase()) && userCustomer=="1") && 
                    <MenuItem text='Security Admin Events' data={{ route: '/security/securityAdminEvents' }} cssClass = "MenuItem" />
                  }
                </MenuItem>
              </MenuItem>
            }
            <MenuItem text='Customer' cssClass = "MenuItem" cssStyle={{ color: 'black'}} >
              <MenuItem text='Customers' data={{ route: '/admin/customers' }} cssClass = "MenuItem" />
            </MenuItem>
            <MenuItem text='Fleet' cssClass = "MenuItem" cssStyle={{ color: 'black' }}>
              <MenuItem text='Asset Management' data={{ route: '/fleet/AssetManagement' }} cssClass = "MenuItem"/>
              <MenuItem text='Fleet Dashboard' data={{ route: '/fleet' }} cssClass = "MenuItem" />
            </MenuItem>
            {/* <MenuItem text='Synop' cssClass = "MenuItem" cssStyle={{ color: 'black' }}>
              <MenuItem text='Charging Management' data={{ route: '/chargers/chargerManagement' }} cssClass = "MenuItem"/>
              <MenuItem text='Charging Dashboard' data={{ route: '/chargers' }} cssClass = "MenuItem"/>
              <MenuItem text='Depot Management' data={{ route: '/depots/depotManagement' }} cssClass = "MenuItem" />
            </MenuItem> */}
            <MenuItem text='Account' cssClass = "MenuItem" cssStyle={{ color: 'black' }}>
              <MenuItem text='Manage Profile' data={{ popup: 'manage-profile' }} cssClass = "MenuItem"/>
              <MenuItem text='Change Preferences' data={{ popup: 'change-preferences' }} cssClass = "MenuItem"/>
              <MenuItem text='Change Password' data={{ popup: 'change-password' }} cssClass = "MenuItem"/>
            </MenuItem>
            {user
                ?
                  <MenuItem text= {user} cssClass = "MenuItem" cssStyle={{ color: 'black'}}>
                    <MenuItem text='Signout' data={{ action: '/auth/signout' }} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
                  </MenuItem>
                :
                  <MenuItem text='Login' data={{ route: '/auth/signin'}} cssClass = "MenuItem" cssStyle={{ color: 'black'}} />
              }
          </Menu>
        </AppBarSection>
        <AppBarSpacer style={{ width: 4 }} />
      </AppBar>
      <Popup
        visible = {visible}
        title = {title}
        body = {dialog}
        toggleDialogClose = {toggleDialogClose}
        closeButtonName = {closeButtonName}
        extraButtons = {extraButtons}
      />
      <style>{`
        body {
            background: #dfdfdf;
        }
        .title {
            font-size: 18px;
            margin: 0;
        }
        .MenuItem {
            color: black;
            background-color: white;
        }
     `}</style>
    </Fragment>
  );
};

export default Navbar;
